import { useState } from "react";
import { WorkOrderMessages } from "../contacts/WorkOrderMessages";
import { WorkOrderFlow } from "./WorkOrderFlow";
import { WorkOrderDelivery } from "./WorkOrderDelivery";
import { WorkOrderPhotos } from "./WorkOrderPhotos";
import { Link, useParams } from "react-router-dom";
import s from "./WorkOrders.module.css";
import { Page } from "../../layout/AppShell";
import { useSession } from "../../app/SessionContext";
import {
  useAddWorkOrderNote,
  useClassifyWorkOrder,
  useSetWorkOrderSla,
  useTransitionWorkOrder,
  useWorkOrder,
  useWorkOrderHistory,
} from "../../api/queries";
import { useReporter } from "../../assistant/report";
import { hasPermission, PERMISSION } from "../../api/dataSource";
import {
  ageDays,
  isOpen,
  isTerminal,
  KIND_LABEL,
  legalTransitions,
  STATUS_LABEL,
  TRANSITION_VERB,
  type WorkOrder,
  type WorkOrderStatus,
} from "../../domain/workOrders";
import { SLA_LABEL, slaDeadline, slaRemaining, slaState } from "../../domain/workOrderReport";
import { SLA_TONE } from "./slaTone";
import { Button, Card, ErrorState, Input, PriorityBadge, Select, Skeleton, StatusBadge } from "../../design/components";
import { formatDate, formatDateTime } from "../../lib/format";
import { WorkOrderParts } from "./WorkOrderParts";
import { locale, t } from "../../i18n";

import {
  AREA_LABEL,
  AREAS,
  areaOf,
  contractorOf,
  TRADE_LABEL,
  TRADES,
  tradeOf,
  type Area,
  type Trade,
} from "../../domain/workOrderClass";

export function WorkOrderDetailPage() {
  const { id = "" } = useParams();
  const { site } = useSession();
  const wo = useWorkOrder(id);
  const history = useWorkOrderHistory(id);

  if (wo.isPending) {
    return (
      <Page eyebrow={t("{code} · work order", { code: site.code })} title="…">
        <Skeleton rows={6} height={18} />
      </Page>
    );
  }
  if (wo.isError) {
    return (
      <Page
        eyebrow={t("{code} · work order", { code: site.code })}
        title={id}
        actions={<Link to="/work-orders">{t("← All work orders")}</Link>}
      >
        <ErrorState error={wo.error} retry={() => void wo.refetch()} />
      </Page>
    );
  }
  const w = wo.data;

  return (
    <Page
      eyebrow={t("{code} · work order · {kind}", { code: site.code, kind: t(KIND_LABEL[w.kind]) })}
      title={
        <span style={{ display: "inline-flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <span className="mono">{w.number}</span>
          <PriorityBadge priority={w.priority} />
          <StatusBadge status={w.status} />
        </span>
      }
      actions={<Link to="/work-orders">{t("← All work orders")}</Link>}
    >
      <WorkOrderFlow current={w.status} />
      <WorkOrderDelivery wo={w} />
      <div className={s.detail}>
        <div style={{ display: "grid", gap: "var(--gap)" }}>
          <Card
            title={w.title}
            sub={
              w.alertId ? (
                <Link to={`/alerts?open=${w.alertId}`}>{t("Raised from an alert — why did it fire?")}</Link>
              ) : undefined
            }
          >
            <Classification wo={w} />
            <div className={s.facts} style={{ marginTop: 12 }}>
              <Fact label={t("Asset")} value={w.assetTag} mono />
              <Fact label={t("Zone")} value={w.zoneCode} mono />
              <Fact label={t("Crew")} value={w.crew} />
              <Fact label={t("Requested by")} value={w.requestedBy} />
              <Fact
                label={t("Position")}
                value={w.point ? `${w.point.lat.toFixed(5)}, ${w.point.lon.toFixed(5)}` : undefined}
                mono
              />
              <Fact label={t("Opened")} value={formatDate(w.openedAt)} />
              <Fact label={t("Due")} value={w.dueAt ? formatDateTime(w.dueAt) : undefined} />
              <Fact label={t("Started")} value={w.startedAt ? formatDate(w.startedAt) : undefined} />
              <Fact
                label={t("Completed")}
                value={
                  w.completedAt
                    ? w.completedBy
                      ? t("{date} by {name}", { date: formatDate(w.completedAt), name: w.completedBy })
                      : formatDate(w.completedAt)
                    : undefined
                }
              />
              <Fact
                label={t("Verified")}
                value={
                  w.verifiedAt && w.status === "verified"
                    ? w.verifiedVia === "photo"
                      ? t("{date} by photo", { date: formatDate(w.verifiedAt) })
                      : w.verifiedBy
                        ? t("{date} by {name}", { date: formatDate(w.verifiedAt), name: w.verifiedBy })
                        : formatDate(w.verifiedAt)
                    : undefined
                }
              />
              <Fact label={t("Age")} value={t("{n} days", { n: ageDays(w) })} />
              <Fact
                label={t("Actual cost")}
                value={w.costActual != null ? w.costActual.toLocaleString(locale()) : undefined}
                mono
              />
            </div>
            {w.description && <div className={s.notes}>{w.description}</div>}
          </Card>

          {w.defects?.length || w.tasks?.length ? (
            <Card title="Scope" sub="Defects and tasks in this order">
              {w.defects && w.defects.length > 0 && (
                <div className={s.list}>
                  {w.defects.map((d) => (
                    <div key={d.id} className={s.listRow}>
                      <span className="mono muted">S{d.severity}</span>
                      <span>
                        {d.summary}
                        {d.defectType && <span className="muted"> · {d.defectType}</span>}
                      </span>
                      <span className="mono muted">{d.status}</span>
                    </div>
                  ))}
                </div>
              )}
              {w.tasks && w.tasks.length > 0 && (
                <div className={s.list} style={{ marginTop: 8 }}>
                  {w.tasks.map((task) => (
                    <div key={task.seq} className={s.listRow}>
                      <span className="mono muted">{task.seq}</span>
                      <span>{task.description}</span>
                      <span className="mono muted">{task.status}</span>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          ) : null}

          <WorkOrderPhotos wo={w} />
          <WorkOrderParts wo={w} history={history.data ?? []} />

          <WorkOrderMessages workOrderId={w.id} />

          <Card title="History" sub="Every change and note, with who and when">
            <NoteBox wo={w} />
            {history.isPending ? (
              <Skeleton rows={4} />
            ) : history.isError ? (
              <ErrorState error={history.error} retry={() => void history.refetch()} />
            ) : (
              <div className={s.history}>
                {[...history.data].reverse().map((e, i) => (
                  <div key={e.id} className={s.event}>
                    <div className={`${s.eventDot} ${i === 0 ? s.eventDotNow : ""}`} />
                    <div>
                      <div>
                        {e.kind === "note" ? (
                          <b>{t("Note")}</b>
                        ) : e.from && e.from === e.to ? (
                          <b>{t("Updated")}</b>
                        ) : e.from ? (
                          <>
                            {t(STATUS_LABEL[e.from])} → <b>{t(STATUS_LABEL[e.to])}</b>
                          </>
                        ) : (
                          <b>{t("Raised as {status}", { status: t(STATUS_LABEL[e.to]) })}</b>
                        )}
                        <span className="muted"> {t("by {name}", { name: e.by })}</span>
                      </div>
                      <div className={s.eventMeta}>{formatDateTime(e.at)}</div>
                      {e.note && <div className={s.eventNote}>{e.note}</div>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        <div style={{ display: "grid", gap: "var(--gap)", alignContent: "start" }}>
          <Actions wo={w} />
          <Sla wo={w} />
        </div>
      </div>
    </Page>
  );
}

/**
 * The order's SLA: where it stands, and — while the work is still open — a
 * way to set or change it, with a reason the platform records. Once the order
 * is completed the SLA is part of the outcome and cannot move.
 */
function Sla({ wo }: { wo: WorkOrder }) {
  const { principal, serverTime, source } = useSession();
  const setSla = useSetWorkOrderSla(wo.id);
  const [hours, setHours] = useState("");
  const [reason, setReason] = useState("");
  const [refusal, setRefusal] = useState<string | null>(null);
  const now = new Date(serverTime);
  const state = slaState(wo, now);
  const deadline = slaDeadline(wo);
  const remaining = slaRemaining(wo, now);
  const canWrite = hasPermission(principal, PERMISSION.workOrdersWrite);
  const open = isOpen(wo.status);

  return (
    <Card title="Service level (SLA)" sub="Time allowed to complete this order">
      <div className={s.facts}>
        <div className={s.fact}>
          <span className={s.factLabel}>{t("Status")}</span>
          <span className={s.factValue} style={{ color: SLA_TONE[state], fontWeight: 600 }}>
            {t(SLA_LABEL[state])}
          </span>
        </div>
        <Fact label={t("Window")} value={wo.slaHours != null ? `${wo.slaHours} h` : undefined} mono />
        <Fact label={t("Deadline")} value={deadline ? formatDateTime(deadline.toISOString()) : undefined} />
        <Fact label={t("Against it")} value={remaining ?? undefined} />
      </div>
      {open ? (
        <form
          className={s.actions}
          style={{ marginTop: 10 }}
          onSubmit={(e) => {
            e.preventDefault();
            setRefusal(null);
            setSla.mutate(
              { slaHours: Number(hours), reason: reason.trim() },
              {
                onSuccess: () => {
                  setHours("");
                  setReason("");
                },
                onError: (err) => setRefusal(err instanceof Error ? err.message : String(err)),
              },
            );
          }}
        >
          <div className={s.actionRow}>
            <Input
              type="number"
              min={1}
              max={8760}
              step={1}
              required
              value={hours}
              onChange={(e) => setHours(e.target.value)}
              placeholder={wo.slaHours != null ? t("New window, hours") : t("Window, hours")}
              aria-label={t("SLA hours")}
              style={{ width: 150 }}
            />
            <Input
              required
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t("Reason")}
              aria-label={t("Reason for the SLA")}
              style={{ flex: 1, minWidth: 140 }}
            />
            <Button
              type="submit"
              disabled={!canWrite || setSla.isPending || !hours || !reason.trim()}
              title={canWrite ? undefined : t("Requires {permission}", { permission: PERMISSION.workOrdersWrite })}
            >
              {wo.slaHours != null ? t("Change SLA") : t("Set SLA")}
            </Button>
          </div>
          {refusal && (
            <div className={s.refusal} role="alert">
              {source.kind === "mock"
                ? t("Refused by the sample: {reason}", { reason: refusal })
                : t("Refused by the platform: {reason}", { reason: refusal })}
            </div>
          )}
        </form>
      ) : (
        <div className="muted" style={{ fontSize: 13, marginTop: 8 }}>
          {t("The SLA can't be changed once an order is {status}.", {
            status: t(STATUS_LABEL[wo.status]).toLowerCase(),
          })}
        </div>
      )}
    </Card>
  );
}

/** Write a note on the order; it lands in the history with your name and the time. */
function NoteBox({ wo }: { wo: WorkOrder }) {
  const { principal, source } = useSession();
  const add = useAddWorkOrderNote(wo.id);
  const canWrite = hasPermission(principal, PERMISSION.workOrdersWrite);
  const [text, setText] = useState("");
  const [error, setError] = useState<string | null>(null);
  return (
    <form
      className={s.actionRow}
      style={{ marginBottom: 14 }}
      onSubmit={(e) => {
        e.preventDefault();
        if (!canWrite) return setError(t("You need the Supervisor or Technician role to change this."));
        setError(null);
        add.mutate(text, {
          onSuccess: () => setText(""),
          onError: (err) =>
            setError(
              source.kind === "mock"
                ? t("Refused by the sample: {reason}", { reason: err instanceof Error ? err.message : String(err) })
                : err instanceof Error
                  ? err.message
                  : String(err),
            ),
        });
      }}
    >
      <Input
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={t("Write a note")}
        aria-label={t("Write a note")}
        maxLength={2000}
        style={{ flex: 1, minWidth: 200 }}
      />
      <Button type="submit" size="sm" disabled={add.isPending || !text.trim()}>
        {t("Add note")}
      </Button>
      {error && (
        <div className={s.refusal} role="alert" style={{ flexBasis: "100%" }}>
          {error}
        </div>
      )}
    </form>
  );
}

/** Where the work is, its trade and its contractor — and a way to change them. */
function Classification({ wo }: { wo: WorkOrder }) {
  const { principal, source } = useSession();
  const classify = useClassifyWorkOrder(wo.id);
  const canWrite = hasPermission(principal, PERMISSION.workOrdersWrite);
  const area = areaOf(wo);
  const trade = tradeOf(wo);
  const [editing, setEditing] = useState(false);
  const [a, setA] = useState<Area | "">(area.value ?? "");
  const [tr, setTr] = useState<Trade | "">(trade.value ?? "");
  const [c, setC] = useState(contractorOf(wo) ?? "");
  const [refusal, setRefusal] = useState<string | null>(null);
  const derived = t("(worked out)");

  return (
    <div>
      <div className={s.facts}>
        <Fact
          label={t("Area")}
          value={area.value ? `${t(AREA_LABEL[area.value])}${area.derived ? ` ${derived}` : ""}` : undefined}
        />
        <Fact
          label={t("Trade")}
          value={trade.value ? `${t(TRADE_LABEL[trade.value])}${trade.derived ? ` ${derived}` : ""}` : undefined}
        />
        <Fact label={t("Contractor")} value={contractorOf(wo) ?? t("Own team")} />
        <div className={s.fact} style={{ alignSelf: "end" }}>
          {!editing && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() =>
                canWrite
                  ? setEditing(true)
                  : setRefusal(t("You need the Supervisor or Technician role to change this."))
              }
            >
              {t("Change")}
            </Button>
          )}
        </div>
      </div>
      {editing && (
        <form
          className={s.actionRow}
          style={{ marginTop: 10 }}
          onSubmit={(e) => {
            e.preventDefault();
            setRefusal(null);
            classify.mutate(
              { area: a, trade: tr, contractor: c },
              {
                onSuccess: () => setEditing(false),
                onError: (err) => setRefusal(err instanceof Error ? err.message : String(err)),
              },
            );
          }}
        >
          <Select value={a} onChange={(e) => setA(e.target.value as Area | "")} aria-label={t("Area")}>
            <option value="">{t("Area not set")}</option>
            {AREAS.map((k) => (
              <option key={k} value={k}>
                {t(AREA_LABEL[k])}
              </option>
            ))}
          </Select>
          <Select value={tr} onChange={(e) => setTr(e.target.value as Trade | "")} aria-label={t("Trade")}>
            <option value="">{t("Trade not set")}</option>
            {TRADES.map((k) => (
              <option key={k} value={k}>
                {t(TRADE_LABEL[k])}
              </option>
            ))}
          </Select>
          <Input
            value={c}
            onChange={(e) => setC(e.target.value)}
            placeholder={t("Contractor (blank = own team)")}
            aria-label={t("Contractor")}
            style={{ flex: 1, minWidth: 160 }}
          />
          <Button type="submit" variant="primary" disabled={classify.isPending}>
            {t("Save")}
          </Button>
          <Button onClick={() => setEditing(false)}>{t("Cancel")}</Button>
        </form>
      )}
      {refusal && (
        <div className={s.refusal} role="alert" style={{ marginTop: 8 }}>
          {editing
            ? source.kind === "mock"
              ? t("Refused by the sample: {reason}", { reason: refusal })
              : t("Refused by the platform: {reason}", { reason: refusal })
            : refusal}
        </div>
      )}
    </div>
  );
}

function Fact({ label, value, mono }: { label: string; value?: string; mono?: boolean }) {
  return (
    <div className={s.fact}>
      <span className={s.factLabel}>{label}</span>
      <span className={`${s.factValue} ${mono ? "mono" : ""}`}>{value ?? <span className="muted">—</span>}</span>
    </div>
  );
}

function Actions({ wo }: { wo: WorkOrder }) {
  const { principal, source } = useSession();
  const transition = useTransitionWorkOrder(wo.id);
  const reporter = useReporter();
  const [note, setNote] = useState("");
  const [refusal, setRefusal] = useState<string | null>(null);

  /* The platform's word on what is legal, when it gave one; the domain rule otherwise. */
  const allowed = wo.allowedTransitions ?? [...legalTransitions(wo.status)];
  const canWrite = hasPermission(principal, PERMISSION.workOrdersWrite);
  const canVerify = hasPermission(principal, PERMISSION.workOrdersVerify);

  const go = (to: WorkOrderStatus) => {
    setRefusal(null);
    const text = note.trim() || undefined;
    transition.mutate(
      { to, note: text, onHoldReason: to === "on_hold" ? text : undefined },
      {
        onSuccess: (moved) => {
          setNote("");
          /* After the platform has confirmed, never before: she reports the
             record, not the request. */
          reporter.workOrderMoved(moved, wo.status);
        },
        onError: (e) => setRefusal(e instanceof Error ? e.message : String(e)),
      },
    );
  };

  return (
    <Card title="Next step" sub={isTerminal(wo.status) ? undefined : "Only the allowed next steps are shown."}>
      {isTerminal(wo.status) ? (
        <div className={s.terminal}>
          {t("This order is {status} and can't be changed.", {
            status: t(STATUS_LABEL[wo.status]).toLowerCase(),
          })}
        </div>
      ) : (
        <div className={s.actions}>
          <textarea
            className={s.note}
            placeholder={t("Add a note (required when putting an order on hold)")}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            style={{
              padding: "6px 10px",
              border: "1px solid var(--line)",
              borderRadius: "var(--radius-sm)",
              background: "var(--panel2)",
              color: "var(--ink)",
              font: "inherit",
              fontSize: 13.5,
            }}
          />
          <div className={s.actionRow}>
            {allowed
              .filter((to) => to !== "cancelled")
              .map((to) => {
                const needs = to === "verified" ? canVerify : canWrite;
                const key = to === "verified" ? PERMISSION.workOrdersVerify : PERMISSION.workOrdersWrite;
                return (
                  <Button
                    key={to}
                    variant={to === "on_hold" ? "default" : "primary"}
                    disabled={!needs || transition.isPending}
                    onClick={() => go(to)}
                    title={needs ? `→ ${t(STATUS_LABEL[to])}` : t("Requires {permission}", { permission: key })}
                  >
                    {t(TRANSITION_VERB[to])}
                  </Button>
                );
              })}
            {allowed.includes("cancelled") && (
              <Button
                variant="danger"
                disabled={!canWrite || transition.isPending}
                onClick={() => go("cancelled")}
                title={
                  canWrite
                    ? `→ ${t("Canceled")}`
                    : t("Requires {permission}", { permission: PERMISSION.workOrdersWrite })
                }
              >
                {t(TRANSITION_VERB.cancelled)}
              </Button>
            )}
          </div>
          {refusal && (
            <div className={s.refusal} role="alert">
              {source.kind === "mock"
                ? t("Refused by the sample: {reason}", { reason: refusal })
                : t("Refused by the platform: {reason}", { reason: refusal })}
            </div>
          )}
          {/* a button that cannot be pressed says why, where it can be read — not in a tooltip */}
          {!canWrite && allowed.some((to) => to !== "verified") && (
            <div className={s.why} role="note">
              {t(
                "Your account can view this order but can't move it. Ask an administrator to add the Supervisor or Technician role to your account in Administration › People.",
              )}
            </div>
          )}
          {wo.status === "completed" && (
            <div className={s.why} role="note">
              {canVerify
                ? t("Verify only if you didn't do the work yourself — a second person must check it.")
                : t(
                    "A Supervisor or the Accountable Manager verifies this order — someone other than the person who completed the work.",
                  )}
            </div>
          )}
          {wo.status === "in_progress" && (
            <div className="muted" style={{ fontSize: 13 }}>
              {t(
                'Where the defect was raised with a photograph, completion needs an "after" photograph on the platform.',
              )}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
